/**
 * useBacktrackrContext.ts - Hook for automatic context management
 * 
 * CHANGE HISTORY:
 * - 251116_2312 danielbeer: Create hook for automatic context setup/cleanup
 * 
 * Automatically sets and clears Backtrackr context for a component.
 * Simplifies context-aware undo implementation to a single line.
 */

import { useEffect } from 'react';
import { useBacktrackr } from '../BacktrackrContext';

/**
 * Hook to automatically manage Backtrackr context
 * 
 * Sets the active context on mount and clears it on unmount.
 * 
 * @param contextId - Unique context identifier (e.g., 'outliner:view:abc123')
 * @returns Backtrackr instance (same as useBacktrackr())
 * 
 * @example
 * ```typescript
 * // Instead of:
 * const backtrackr = useBacktrackr();
 * useEffect(() => {
 *   backtrackr.setActiveContext('my-context');
 *   return () => backtrackr.clearActiveContext();
 * }, [backtrackr]);
 * 
 * // Just use:
 * const backtrackr = useBacktrackrContext('my-context');
 * ```
 * 
 * @example
 * ```typescript
 * // With dynamic context ID
 * const backtrackr = useBacktrackrContext(`outliner:view:${viewId}`);
 * ```
 */
export function useBacktrackrContext(contextId: string) {
  const backtrackr = useBacktrackr();
  
  useEffect(() => {
    if (!backtrackr) return;
    
    console.log('[Backtrackr] Setting context:', contextId);
    
    // Set active context
    if (typeof backtrackr.setActiveContext === 'function') {
      backtrackr.setActiveContext(contextId);
    }
    
    // Cleanup: clear context on unmount
    return () => {
      console.log('[Backtrackr] Clearing context:', contextId);
      if (typeof backtrackr.clearActiveContext === 'function') {
        backtrackr.clearActiveContext();
      }
    };
  }, [contextId, backtrackr]);
  
  return backtrackr;
}
